import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/api";
import { useI18n, localeSort } from "@/i18n";
import { useSeo } from "@/seo";
import { playerLink } from "@/components/PlayerCard";

const mono = "font-mono2 tracking-[.04em]";
const th = `${mono} border-b border-[var(--d-line)] px-3 py-2 text-left text-[9px] uppercase text-[var(--d-muted)]`;
const td = `${mono} border-b border-[var(--d-line)] px-3 py-2 text-[11px] text-[var(--d-text)]`;
const ctl = `${mono} w-full border border-[var(--d-line)] bg-[var(--d-panel)] px-2.5 py-1.5 text-[10.5px] text-[var(--d-text)] outline-none focus:border-[var(--d-accent)]`;
const PAGE = 50;

export default function GraduatedCohort() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [f, setF] = useState({ league: "", club: "", season: "", q: "" });
  const [page, setPage] = useState(0);
  const [seoSeason, setSeoSeason] = useState("");
  useSeo(t("cohort.seo_title", { season: seoSeason }).replace("  ", " "), t("cohort.seo_desc"), "/graduated");

  const params = useMemo(() => {
    const p = { limit: PAGE, skip: page * PAGE };
    if (f.league) p.league = f.league;
    if (f.club) p.club = f.club;
    if (f.season) p.season = f.season;
    if (f.q.trim()) p.q = f.q.trim();
    return p;
  }, [f, page]);

  useEffect(() => {
    // Une requête interrompue n'est pas un cohorte vide : on réessaie une fois et on ne
    // fabrique jamais `total: 0` (même règle que la watchlist, 22/09/2026).
    let alive = true;
    const ask = (tries) => {
      api.get("/players/graduated-cohort", { params })
        .then((r) => { if (!alive) return; setData(r.data); setSeoSeason(String((r.data.seasons || [])[0] || "")); })
        .catch(() => { if (alive && tries > 0) setTimeout(() => ask(tries - 1), 1500); });
    };
    const id = setTimeout(() => ask(2), 250);
    return () => { alive = false; clearTimeout(id); };
  }, [params]);

  const set = (patch) => { setPage(0); setF({ ...f, ...patch }); };
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="dash-scope mx-auto max-w-[1200px] pt-8" data-testid="graduated-page">
      <div className="mb-6 border-b border-[var(--d-line)] pb-4">
        <h1 className="font-sg text-3xl font-bold uppercase tracking-[-0.035em] text-[var(--d-text)] sm:text-4xl">{t("cohort.title")}</h1>
        <p className={`${mono} mt-2 max-w-2xl text-[11px] text-[var(--d-muted)]`}>{t("cohort.intro")}</p>
        {data && <p className={`${mono} mt-1 text-[9px] uppercase text-[var(--d-muted)]`} data-testid="cohort-total">{total} {t("cohort.count_label")}</p>}
      </div>

      <div className="mb-4 grid grid-cols-2 gap-2 lg:grid-cols-4" data-testid="cohort-filters">
        <div className="relative col-span-2 lg:col-span-1">
          <Search className="pointer-events-none absolute left-2.5 top-2 h-3.5 w-3.5 text-[var(--d-muted)]" />
          <input data-testid="cohort-search-input" className={`${ctl} pl-8`} placeholder={t("filters.search")} value={f.q} onChange={(e) => set({ q: e.target.value })} />
        </div>
        <select data-testid="cohort-league-select" className={ctl} value={f.league} onChange={(e) => set({ league: e.target.value })}>
          <option value="">{t("filters.league")} — {t("filters.any")}</option>
          {localeSort(data?.leagues || [], lang).map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select data-testid="cohort-club-select" className={ctl} value={f.club} onChange={(e) => set({ club: e.target.value })}>
          <option value="">{t("filters.club")} — {t("filters.any")}</option>
          {localeSort(data?.clubs || [], lang).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="cohort-season-select" className={ctl} value={f.season} onChange={(e) => set({ season: e.target.value })}>
          <option value="">{t("cohort.col_season")} — {t("filters.any")}</option>
          {(data?.seasons || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {!data && <p className={`${mono} text-sm text-[var(--d-muted)]`}>{t("common.loading")}</p>}
      {data && data.players.length === 0 && <p className={`${mono} text-[11px] text-[var(--d-muted)]`} data-testid="cohort-empty">{t("cohort.empty")}</p>}
      {data && data.players.length > 0 && (
        <div className="overflow-x-auto border border-[var(--d-line)] bg-[var(--d-panel)]">
          <table className="w-full min-w-[640px] border-collapse" data-testid="cohort-table">
            <thead>
              <tr>
                <th className={th}>{t("cohort.col_player")}</th>
                <th className={th}>{t("cohort.col_club")}</th>
                <th className={th}>{t("cohort.col_league")}</th>
                <th className={th}>{t("cohort.col_season")}</th>
                <th className={th}>{t("cohort.col_graduated")}</th>
                <th className={`${th} text-right`} title={t("cohort.hype_help")}>{t("cohort.col_hype")}</th>
              </tr>
            </thead>
            <tbody>
              {data.players.map((p) => (
                <tr key={`${p.provider}:${p.player_id}`} data-testid="cohort-row" className="hover:bg-[#1A2334]">
                  <td className={td}><Link to={playerLink(p)} className="font-sg text-[13px] font-bold uppercase tracking-[-0.01em] hover:text-[var(--d-accent)]">{p.name}</Link></td>
                  <td className={td}>{p.club_name || "—"}</td>
                  <td className={td}>{p.league_name || "—"}</td>
                  <td className={`${td} text-[var(--d-muted)]`}>{p.season || "—"}</td>
                  <td className={`${td} text-[var(--d-muted)]`}>{p.graduated_at ? p.graduated_at.slice(0, 10) : "—"}</td>
                  <td className={`${td} text-right font-bold text-[var(--d-accent)]`}>{p.signal_score ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE && (
        <div className="mt-4 flex items-center gap-3" data-testid="cohort-pagination">
          <button data-testid="cohort-prev-page" disabled={page === 0} onClick={() => setPage(page - 1)}
            className={`${mono} border border-[var(--d-line)] px-3 py-1.5 text-[10px] uppercase text-[var(--d-text)] transition-colors hover:border-[var(--d-accent)] disabled:opacity-40`}>
            {t("cohort.prev")}
          </button>
          <span className={`${mono} text-[10px] uppercase text-[var(--d-muted)]`} data-testid="cohort-page-label">{page + 1} / {pages}</span>
          <button data-testid="cohort-next-page" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}
            className={`${mono} border border-[var(--d-line)] px-3 py-1.5 text-[10px] uppercase text-[var(--d-text)] transition-colors hover:border-[var(--d-accent)] disabled:opacity-40`}>
            {t("cohort.next")}
          </button>
        </div>
      )}

      <p className={`${mono} mt-3 text-[9px] uppercase text-[var(--d-muted)]`}>
        {t("cohort.hype_help")} · <Link to="/how-it-works" className="underline hover:text-[var(--d-accent)]" data-testid="cohort-how-link">{t("hype.link")}</Link>
      </p>
    </div>
  );
}
